import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

export const TOKEN_KEY = "cliqo_token";
export const REFRESH_TOKEN_KEY = "cliqo_refresh_token";
export const REFRESH_TOKEN_EXPIRES_KEY = "cliqo_refresh_token_expires";

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(
  token: string,
  refreshToken: string,
  refreshTokenExpiresAt: string,
): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(REFRESH_TOKEN_EXPIRES_KEY, refreshTokenExpiresAt);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_EXPIRES_KEY);
}

export function isRefreshTokenExpired(): boolean {
  const expiresAt = localStorage.getItem(REFRESH_TOKEN_EXPIRES_KEY);
  if (!expiresAt) return true;
  return new Date(expiresAt).getTime() <= Date.now();
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

// In development, leave baseURL empty so requests are relative to the Vite dev
// server and go through its proxy (avoids CORS entirely).
//
// In production the SPA always talks to the API on api.cliqo.link. clq.sh is
// the short-link/redirect domain only and must never be used as the API base.
// VITE_API_BASE_URL can still override this, but if it's empty/unset a prod
// build falls back to api.cliqo.link rather than relative (broken) URLs.
const FALLBACK_API_BASE = import.meta.env.PROD ? "https://api.cliqo.link" : "";
const BASE_URL = import.meta.env.VITE_API_BASE_URL || FALLBACK_API_BASE;

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 30_000,
});

// ---------------------------------------------------------------------------
// Request interceptor — attach Bearer token
// ---------------------------------------------------------------------------

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — handle 401 with token refresh
// ---------------------------------------------------------------------------

interface QueueItem {
  resolve: (value: string) => void;
  reject: (reason: unknown) => void;
}

let isRefreshing = false;
let failedRequestQueue: QueueItem[] = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedRequestQueue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else if (token) {
      item.resolve(token);
    }
  });
  failedRequestQueue = [];
}

// Extend axios config to track retry attempts
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError): Promise<AxiosResponse> => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    // If the error is not 401 or there's no original request, reject immediately
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Auth endpoints (login, refresh, registration, password reset) legitimately
    // return 401/credential errors — there's no session to refresh. Let the
    // caller handle them instead of clearing tokens and reloading the page
    // (which would discard the inline error message on the login form).
    const requestUrl = originalRequest.url ?? "";
    if (
      requestUrl.includes("/auth/login") ||
      requestUrl.includes("/auth/refresh") ||
      requestUrl.includes("/auth/password") ||
      requestUrl.includes("/users/register") ||
      // Passwordless passkey sign-in is unauthenticated — a 401 means the
      // passkey was rejected, not that a session expired. Let the login page
      // surface the error instead of redirecting/reloading.
      requestUrl.includes("/passkeys/authentication")
    ) {
      return Promise.reject(error);
    }

    // Avoid infinite retry loops (e.g. if the refresh endpoint itself returns 401)
    if (originalRequest._retry) {
      clearTokens();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // If a refresh is already in progress, queue this request
    if (isRefreshing) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        failedRequestQueue.push({
          resolve: (token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(apiClient(originalRequest));
          },
          reject,
        });
      });
    }

    // Check if refresh token exists and hasn't expired
    const refreshToken = getRefreshToken();
    if (!refreshToken || isRefreshTokenExpired()) {
      clearTokens();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // Mark request as retried and start refreshing
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Use the absolute backend URL for the refresh call so it bypasses any
      // stale interceptor state. Falls back to relative path when no explicit
      // base URL is set (i.e. development behind the Vite proxy).
      const refreshBase = import.meta.env.VITE_API_BASE_URL || FALLBACK_API_BASE;
      const { data } = await axios.post<{
        token: string;
        refreshToken: string;
        refreshTokenExpiresAt: string;
      }>(
        `${refreshBase}/app/auth/refresh`,
        { refreshToken },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 10_000,
        },
      );

      const {
        token,
        refreshToken: newRefreshToken,
        refreshTokenExpiresAt,
      } = data;
      setTokens(token, newRefreshToken, refreshTokenExpiresAt);

      // Update the default Authorization header
      apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // Update the retried request's header
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${token}`;
      }

      // Resolve all queued requests with the new token
      processQueue(null, token);

      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearTokens();
      window.location.href = "/login";
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

// ---------------------------------------------------------------------------
// Typed request helpers
// ---------------------------------------------------------------------------

export async function get<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

export async function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

export async function patch<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.patch<T>(url, data, {
    ...config,
    headers: {
      "Content-Type": "application/merge-patch+json",
      ...config?.headers,
    },
  });
  return response.data;
}

export async function put<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
}

export async function del<T = void>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
}

export default apiClient;
